import type { ReactNode } from 'react';
import { Button, Modal, ModalBody, ModalFooter } from '@customerio/pluma-components/react';

/** Which confirmation this is. Also names its test attributes, as Ember's did. */
export type SegmentModalKind = 'delete' | 'disable' | 'clear';

type SegmentConfirmModalProps = {
  kind: SegmentModalKind;
  isOpen: boolean;
  title: string;
  confirmLabel: string;
  /** The permission this particular confirmation needs. */
  isAllowed: boolean;
  isBusy: boolean;
  onClose: () => void;
  onConfirm: () => void;
  children: ReactNode;
};

/**
 * One confirmation.
 *
 * The three on this screen differ only in copy, the permission they need, and what
 * they do, so the footer and the disabled rules live here once. Cancel is always
 * available while nothing is running, whatever the reader may not do.
 */
export function SegmentConfirmModal({
  kind,
  isOpen,
  title,
  confirmLabel,
  isAllowed,
  isBusy,
  onClose,
  onConfirm,
  children,
}: SegmentConfirmModalProps) {
  const bodyAttribute = `data-test-segment-in-${kind}-modal-confirm`;
  const confirmAttribute = `data-test-segment-in-${kind}-connection`;

  return (
    <Modal isOpen={isOpen} title={title} onClose={onClose}>
      <ModalBody {...{ [bodyAttribute]: isOpen ? 'visible' : 'hidden' }}>{children}</ModalBody>
      <ModalFooter>
        <Button data-test-segment-in-close-modal isDisabled={isBusy} variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button
          {...{ [confirmAttribute]: '' }}
          isDisabled={isBusy || !isAllowed}
          isLoading={isBusy}
          variant="primary"
          ml="150"
          onClick={onConfirm}
        >
          {confirmLabel}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
